import { Component, Element, forceUpdate, h, Host, Listen, Prop, State } from '@stencil/core';
import { Facet } from '~model/facet';
import { ListingLinks } from '~model/listing-links';
import { ListingResult } from '~model/listing-result';
import { ListProduct } from '~model/product';
import { get, normalizeUrl } from '~service/shop-service';
import { translate } from '~service/translation-service';

@Component({
  tag: 'uvex-product-list',
})
export class ProductList {
  @Element() el!: HTMLElement;
  @Prop() resultUrl!: string;

  @State() result: ListingResult;
  @State() facets: Facet[] = [];
  @State() links: ListingLinks;
  @State() loading = false;

  async componentWillLoad() {
    await this.load();
  }

  @Listen('facetChanged')
  facetChanged(event: CustomEvent<Facet>) {
    const newFacet = event.detail;
    this.facets = this.facets.map(facet => (facet.label === newFacet.label ? newFacet : facet));
    this.appendActiveFacetsToUrl();
  }

  @Listen('facetResetAll')
  facetResetAll() {
    this.facets = this.facets.map(facet => ({ ...facet, ...{ used: false }, ...{ options: [...facet.options.map(option => ({ ...option, ...{ selected: false } }))] } }));
    this.appendActiveFacetsToUrl();
  }

  @Listen('popstate', { target: 'window' })
  async load() {
    this.loading = true;
    try {
      const currentUrl = new URL(window.location.href);
      const apiUrl = new URL(normalizeUrl(this.resultUrl));
      currentUrl.searchParams.forEach((value, key) => apiUrl.searchParams.append(key, value));

      const { data, links } = await get(apiUrl.toString());
      this.result = data.attributes || {};
      this.facets = data.attributes.facets || [];
      this.links = links || {};
    } catch (e) {
    } finally {
      this.loading = false;
    }
  }

  async loadMore() {
    if (!this.links.next) {
      return;
    }

    this.loading = true;
    try {
      const { data, links } = await get(this.links.next);
      const articles = data.attributes.articles as ListProduct[];
      this.result = Object.assign({}, this.result, { articles: [...this.result.articles, ...articles] });
      this.links = links;
    } catch (e) {
    } finally {
      this.loading = false;
    }
    forceUpdate(this.el);
  }

  private async appendActiveFacetsToUrl() {
    const url = new URL(window.location.href.split('?')[0]);
    const parameter = {};

    const activeFacets = this.facets.filter(facet => facet.used);
    activeFacets.forEach(facet =>
      facet.options
        .filter(option => option.selected)
        .forEach(option => {
          parameter[facet.name] = parameter[facet.name] || [];
          parameter[facet.name].push(option.id);
        }),
    );

    Object.keys(parameter).forEach(name => url.searchParams.append(name, parameter[name].sort().join('|')));
    if (url.toString() === window.location.href) {
      return;
    }
    window.history.replaceState({}, '', url.toString());
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  private renderFacets() {
    if (this.facets.length === 0) {
      return null;
    }
    return <uvex-facet-options facets={this.facets} resultCount={this.result.totalResults} />;
  }

  private renderProducts() {
    return this.result && !!this.result.articles.length ? (
      <div class="products">
        {this.result.articles.map(product => (
          <uvex-product product={product} />
        ))}
      </div>
    ) : (
      <div>{translate('listing.noResults')}</div>
    );
  }

  private renderLoadMore() {
    if (!this.links.next) {
      return;
    }

    const moreCount = this.result.totalResults - this.result.articles.length;
    if (moreCount <= 0) {
      return;
    }

    return (
      <div class="product-list-load-more">
        <a role="button" class="btn btn-ghost btn-icon-plus" onClick={_ => this.loadMore()}>
          {translate('sw.listing.load_more', `${moreCount}`)}
        </a>
      </div>
    );
  }

  private openFacetOptions() {
    document.body.classList.add('facet-options-active');
  }

  render() {
    return (
      <Host class="product-list">
        {this.renderFacets()}
        <div class="product-list-items">
          <div class="product-list-actions" />
          <uvex-product-list-active-facets facets={this.facets} resultCount={this.result.totalResults} />
          <div class="product-list-filter-action">
            <button type="button" class="btn btn-block icon-left" onClick={_ => this.openFacetOptions()}>
              <svg width="14" height="14">
                <use href="#fa-filter"></use>
              </svg>
              {translate('sw.listing.filter.title')}
            </button>
          </div>
          {this.renderProducts()}
          {this.renderLoadMore()}
        </div>
        <uvex-spinner active={this.loading} />
      </Host>
    );
  }
}
